import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/useLanguage';
import type { LegalBlock, LegalSection } from '../../locales/types';
import SecurePhone from './SecurePhone';

const INLINE_PATTERN = /(\{phone\}|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|\n)/g;
const LINK_PATTERN = /^\[([^\]]+)\]\(([^)]+)\)$/;

function useInlineRenderer(): (text: string) => ReactNode[] {
    const { localizedPath } = useLanguage();

    return (text: string) =>
        text
            .split(INLINE_PATTERN)
            .filter((token) => token !== '')
            .map((token, index) => {
                if (token === '\n') return <br key={index} />;
                if (token === '{phone}') return <SecurePhone key={index} />;
                if (token.startsWith('**') && token.endsWith('**')) {
                    return <strong key={index}>{token.slice(2, -2)}</strong>;
                }

                const link = token.match(LINK_PATTERN);
                if (link) {
                    const [, label, href] = link;
                    if (href.startsWith('/')) {
                        return (
                            <Link key={index} to={localizedPath(href)}>
                                {label}
                            </Link>
                        );
                    }
                    const isExternal = href.startsWith('http');
                    return (
                        <a
                            key={index}
                            href={href}
                            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                            {label}
                        </a>
                    );
                }

                return <Fragment key={index}>{token}</Fragment>;
            });
}

function renderBlock(
    block: LegalBlock,
    renderInline: (text: string) => ReactNode[],
    key: number
): ReactNode {
    if ('p' in block) return <p key={key}>{renderInline(block.p)}</p>;
    if ('h3' in block) return <h3 key={key}>{block.h3}</h3>;
    if ('ol' in block) {
        return (
            <ol key={key}>
                {block.ol.map((item, index) => (
                    <li key={index}>{renderInline(item)}</li>
                ))}
            </ol>
        );
    }
    return (
        <ul key={key}>
            {block.ul.map((item, index) => (
                <li key={index}>{renderInline(item)}</li>
            ))}
        </ul>
    );
}

export default function LegalDocument({ sections }: { sections: LegalSection[] }) {
    const renderInline = useInlineRenderer();

    return (
        <>
            {sections.map((section, sectionIndex) => (
                <section key={sectionIndex}>
                    <h2>{section.heading}</h2>
                    {section.blocks.map((block, blockIndex) => renderBlock(block, renderInline, blockIndex))}
                </section>
            ))}
        </>
    );
}
